export default (app) => {
  const images = new Map();
  const downloads = new Map();
  const downloading = new Map();
  const consumers = new Map();
  const dimensions = new Map();
  // Object URL -> src, in creation order, so the oldest can be released.
  const objectURLs = new Map();
  const maxObjectURLs = 500;
  const notifications = new Map();
  let cachedImages;
  let connectionReady;
  // Downloads stalled by a lost connection are aborted; failed images retry when it returns.
  let downloadAbort = new AbortController();
  // The connection event's wait option also runs login/version listeners.
  // Observe real connection changes instead of broadcasting one for each image.
  const waitForConnection = () =>
    (connectionReady ||= new Promise((resolve) => {
      const onConnection = ({ connected }) => {
        if (!connected) return;
        app.connection.off("connection", onConnection);
        connectionReady = undefined;
        resolve();
      };
      app.connection.on("connection", onConnection);
    }));

  const load = (src, fileId, signal) => {
    if (signal?.aborted) return Promise.resolve("error");
    if (downloads.has(src)) {
      const active = downloading.get(src);
      if (active) active.retain(signal);
      return downloads.get(src);
    }
    const controller = new AbortController();
    const signals = new Set();
    const listeners = new Map();
    const retain = (signal) => {
      if (signals.has(signal)) return;
      signals.add(signal);
      if (!signal) return;
      const abort = () => {
        signals.delete(signal);
        if (!signals.size) {
          controller.abort();
          downloads.delete(src);
          downloading.delete(src);
        }
      };
      listeners.set(signal, abort);
      signal.addEventListener("abort", abort, { once: true });
    };
    retain(signal);
    downloading.set(src, { retain, controller });
    const download = (async () => {
      const url = new URL(src, window.location.href);
      let displaySrc = src;
      if (url.origin === window.location.origin && url.pathname.startsWith("/image/")) {
        const cache =
          window.caches && (!url.pathname.startsWith("/image/secure/") || url.searchParams.has("nystemCache"))
            ? await window.caches.open("nystem-images")
            : undefined;
        // Cache identity is the original file, independent of its temporary access token.
        if (fileId && url.pathname.startsWith("/image/secure/") && url.searchParams.has("nystemCache"))
          url.pathname = url.pathname.slice(0, url.pathname.lastIndexOf("/") + 1) + fileId;
        let response = await cache?.match(url.href);
        if (!response) {
          // Data requests go first on a slow link.
          response = await window.fetch(src, {
            priority: "low",
            signal: AbortSignal.any([downloadAbort.signal, controller.signal]),
          });
          if (response.ok && cache && response.headers.get("Content-Type")?.startsWith("image/"))
            await cache.put(url.href, response.clone()).catch(async (error) => {
              if (error.name !== "QuotaExceededError") return console.warn("Image cache write failed", error);
              // Cache keys keep insertion order: release the oldest fifth and retry once.
              const keys = await cache.keys();
              await Promise.all(keys.slice(0, Math.ceil(keys.length / 5)).map((key) => cache.delete(key)));
              await cache
                .put(url.href, response.clone())
                .catch((error) => console.warn("Image cache write failed", error));
            });
        }
        controller.signal.throwIfAborted();
        if (!response.ok) {
          app.event("clientLog", { message: "Image download failed", src, status: response.status });
          return "error";
        }
        const blob = await response.blob();
        controller.signal.throwIfAborted();
        if (downloads.get(src) !== download) {
          app.event("clientLog", { message: "Image download superseded", src });
          return "error";
        }
        displaySrc = URL.createObjectURL(blob);
        objectURLs.set(displaySrc, src);
        // Mounted images keep their decoded pixels; a later request for a released src loads it again.
        for (const [objectURL, objectSrc] of objectURLs) {
          if (objectURLs.size <= maxObjectURLs) break;
          objectURLs.delete(objectURL);
          URL.revokeObjectURL(objectURL);
          downloads.delete(objectSrc);
          dimensions.delete(objectURL);
        }
      }
      return new Promise((resolve) => {
        const image = new window.Image();
        image.onload = () => {
          if (downloads.get(src) !== download) return resolve("error");
          dimensions.set(src, { width: image.naturalWidth, height: image.naturalHeight });
          dimensions.set(displaySrc, dimensions.get(src));
          resolve(displaySrc);
        };
        image.onerror = () => {
          app.event("clientLog", { message: "Image decode failed", src });
          if (objectURLs.delete(displaySrc)) URL.revokeObjectURL(displaySrc);
          resolve("error");
        };
        image.src = displaySrc;
      });
    })()
      .catch((error) => {
        if (!controller.signal.aborted)
          app.event("clientLog", { message: "Image download error", src, error: error.message });
        return "error";
      })
      .then((result) => {
        if (result === "error" && downloads.get(src) === download) downloads.delete(src);
        return result;
      })
      .finally(() => {
        for (const [signal, abort] of listeners) signal.removeEventListener("abort", abort);
        if (downloading.get(src)?.controller === controller) downloading.delete(src);
      });
    downloads.set(src, download);
    return download;
  };

  app.connection.on("imageReady", (query) => {
    const notification = notifications.get(query.requestId);
    if (!notification) return;
    notifications.delete(query.requestId);
    notification(query);
  });
  app.connection.on("connection", ({ connected }) => {
    if (connected !== false) return;
    if (notifications.size)
      app.event("clientLog", { message: `Connection lost, ${notifications.size} image requests failed` });
    for (const notification of notifications.values()) notification({ error: "Connection lost" });
    notifications.clear();
    for (const image of images.values()) image.expiresAt = 0;
    downloadAbort.abort();
    downloadAbort = new AbortController();
  });
  app.on("logout", () => {
    images.clear();
    downloads.clear();
    dimensions.clear();
    for (const objectURL of objectURLs.keys()) URL.revokeObjectURL(objectURL);
    objectURLs.clear();
    cachedImages = undefined;
    for (const notification of notifications.values()) notification({ error: "Logged out" });
    notifications.clear();
  });

  // Cached private images must not outlive the session; runs before the page reloads.
  app.on("clearCacheAndReload", 100, async () => {
    if (!window.caches) return;
    // Downloads in flight would write their images after the purge.
    downloadAbort.abort();
    downloadAbort = new AbortController();
    await Promise.allSettled(downloads.values());
    const cache = await window.caches.open("nystem-images");
    for (const request of await cache.keys())
      if (new URL(request.url).pathname.startsWith("/image/secure/")) await cache.delete(request);
  });

  app.on("imageDimensions", ({ src }) => dimensions.get(src));

  const requestImage = async ({
    src,
    secure,
    cache,
    contentType = "image",
    fieldId = "file",
    width = 0,
    height = 0,
    signal,
  }) => {
    const loadImage = (src, fileId) => load(src, fileId, signal);
    if (signal?.aborted) return { ready: Promise.resolve("error") };
    const url = new URL(src, window.location.href);
    // External images and non-fileManager assets retain their ordinary URL behavior.
    const parts = /^\/image\/(?:\d+x\d+\/)?([^/]+)$/.exec(url.pathname);
    if (url.origin !== window.location.origin || !parts) return { ready: loadImage(src) };
    const fileId = parts[1];
    const sessionid = app.session.user?.sessionid;
    secure = Boolean(secure && sessionid);
    cache = Boolean(secure && (cache ?? app.contentType[contentType]?.item?.find(({ id }) => id === fieldId)?.cache));
    const key = `${sessionid || "public"}:${contentType}:${fieldId}:${secure}:${cache}:${fileId}`;
    let image = images.get(key);
    if (!image) {
      image = { variants: [], expiresAt: 0 };
      images.set(key, image);
      image.init = (async () => {
        if (secure && !cache) return;
        // Index the cache by filename once instead of scanning every entry for each image.
        cachedImages ||= (
          window.caches ? window.caches.open("nystem-images").then((cache) => cache.keys()) : Promise.resolve([])
        )
          .catch(() => [])
          .then((requests) => {
            const byFilename = new Map();
            for (const { url } of requests) {
              const filename = new URL(url).pathname.split("/").at(-1);
              if (!byFilename.has(filename)) byFilename.set(filename, []);
              byFilename.get(filename).push(new URL(url));
            }
            return byFilename;
          });
        const prepend = `/image/${secure ? "secure/" : ""}`;
        for (const url of (await cachedImages).get(fileId) || []) {
          if (
            url.origin !== window.location.origin ||
            !url.pathname.startsWith(prepend) ||
            (secure && !url.searchParams.has("nystemCache"))
          )
            continue;
          const dimensions = /\/(\d+)x(\d+)\//.exec(url.pathname);
          if (dimensions)
            image.variants.push({
              src: url.href,
              width: Number(dimensions[1]),
              height: Number(dimensions[2]),
              local: true,
            });
          else image.original = { src: url.href, local: true };
        }
      })();
    }
    try {
      await image.init;
      signal?.throwIfAborted();
    } catch (error) {
      if (signal?.aborted) return { ready: Promise.resolve("error") };
      app.event("clientLog", { message: "Image init failed", src, error: error.message });
      images.delete(key);
      return { ready: Promise.resolve("error") };
    }
    // Same rule as the server: a variant counts only up to four times the requested area.
    const select = () =>
      !width || fileId.endsWith(".svg")
        ? image.original
        : image.variants
            .filter(
              (variant) =>
                variant.width >= width - 1 &&
                variant.height >= height - 1 &&
                variant.width * variant.height <= width * height * 4,
            )
            .sort((a, b) => a.width * a.height - b.width * b.height)[0];
    const loadPreview = () => {
      const selected = select();
      if (selected && dimensions.has(selected.src)) return;
      const pixels = selected?.width * selected?.height || width * height || Infinity;
      const preview = image.variants
        .filter((variant) => variant.width * variant.height < pixels && (variant.local || image.expiresAt > Date.now()))
        .sort((a, b) => b.width * b.height - a.width * a.height)[0];
      return preview ? loadImage(preview.src, fileId) : undefined;
    };
    const remember = (result) => {
      if (result.error) return;
      const local = image.variants.filter((variant) => variant.local);
      image.variants = [...local, ...result.variants];
      image.original = {
        src: `/image/${secure ? "secure/" : ""}${image.tokenId || fileId}${result.cache ? "?nystemCache=1" : ""}`,
      };
      image.expiresAt = result.expiresAt ? result.expiresAt - 1000 : Infinity;
    };
    let selected = select();
    if (
      selected &&
      (selected.local || (dimensions.has(selected.src) && downloads.has(selected.src)) || image.expiresAt > Date.now())
    )
      return { preview: loadPreview(), ready: loadImage(selected.src, fileId) };
    if (!app.connection.connected) {
      const selected =
        image.variants
          .filter((variant) => variant.local || (dimensions.has(variant.src) && downloads.has(variant.src)))
          .sort((a, b) => b.width * b.height - a.width * a.height)[0] ||
        (image.original?.local && image.original);
      if (selected) return { ready: loadImage(selected.src, fileId) };
      // The socket can reconnect while the direct download runs.
      const retry = () =>
        (app.connection.connected ? Promise.resolve() : waitForConnection())
          .then(() => requestImage({ src, secure, cache, contentType, fieldId, width, height, signal }))
          .then(({ ready }) => ready);
      // Public images do not need the socket, the server makes the requested size on download.
      if (!secure && navigator.onLine) {
        const variantSrc = width ? `/image/${width}x${height}/${fileId}` : `/image/${fileId}`;
        return {
          ready: loadImage(variantSrc, fileId).then((result) => {
            if (result === "error") return retry();
            if (width) image.variants.push({ src: variantSrc, width, height, local: true });
            else image.original = { src: variantSrc, local: true };
            return result;
          }),
        };
      }
      // Nothing stored locally: report the offline state and request again once connected.
      return { offline: true, ready: retry() };
    }
    // Share discovery for the file, then reevaluate each caller's requested size.
    while (image.pending) {
      const result = await image.pending;
      if (signal?.aborted) return { ready: Promise.resolve("error") };
      if (result.error) return { ready: Promise.resolve("error") };
      selected = select();
      if (
        selected &&
        (selected.local ||
          (dimensions.has(selected.src) && downloads.has(selected.src)) ||
          image.expiresAt > Date.now())
      )
        return { preview: loadPreview(), ready: loadImage(selected.src, fileId) };
    }
    if (secure) image.tokenId ||= `${app.uuid()}.${fileId.split(".").at(-1)}`;
    const requestId = app.uuid();
    let notification;
    const ready = new Promise((resolve) => {
      notification = resolve;
    });
    notifications.set(requestId, notification);
    const abort = () => {
      notifications.delete(requestId);
      if (image.pending === pending) image.pending = undefined;
      notification({ error: "Image request cancelled" });
      if (app.connection.connected) app.connection.emit({ type: "imageRequestCancel", requestId, noCallback: true });
    };
    signal?.addEventListener("abort", abort, { once: true });
    const request = Promise.race([
      (app.connection.connected ? Promise.resolve() : waitForConnection()).then(() =>
        notifications.has(requestId)
          ? app.connection.emit({
              type: "imageRequest",
              requestId,
              fileId,
              tokenId: image.tokenId,
              contentType,
              fieldId,
              secure,
              cache,
              width,
              height,
              sessionid,
            })
          : { error: "Image request cancelled" },
      ),
      ready,
    ]).catch(() => ({ error: "Image request failed" }));
    image.pending = request
      .then(async (result) => {
        if (signal?.aborted) return { error: "Image request cancelled" };
        remember(result);
        if (result.pending) {
          result = await ready;
          remember(result);
        } else {
          notifications.delete(requestId);
        }
        // Connection loss is logged once for all requests by the connection listener.
        if (result.error && !signal?.aborted && result.error !== "Connection lost")
          app.event("clientLog", {
            message: "Image request failed",
            src,
            error: result.error,
            width,
            height,
            secure,
            contentType,
            fieldId,
            invalid: result.invalid,
          });
        return result;
      })
      .catch(() => ({ error: "Image request failed" }))
      .finally(() => {
        notifications.delete(requestId);
        signal?.removeEventListener("abort", abort);
        if (image.pending === pending) image.pending = undefined;
      });
    const pending = image.pending;
    await request;
    return {
      preview: loadPreview(),
      ready: pending.then((result) => {
        if (result.error) return "error";
        selected = select();
        return loadImage(selected?.src || result.src, fileId).then((src) => {
          if (src !== "error" && (!secure || result.cache)) {
            if (selected) selected.local = true;
            else if (!width) image.original.local = true;
          }
          return src;
        });
      }),
    };
  };

  app.on("imageRequest", async (query) => {
    const { signal } = query;
    if (signal?.aborted) return { ready: Promise.resolve("error") };
    const contentType = query.contentType || "image";
    const fieldId = query.fieldId || "file";
    const secure = Boolean(query.secure && app.session.user?.sessionid);
    const cache = Boolean(
      secure && (query.cache ?? app.contentType[contentType]?.item?.find(({ id }) => id === fieldId)?.cache),
    );
    const url = new URL(query.src, window.location.href);
    const key = JSON.stringify([
      app.session.user?.sessionid,
      url.origin,
      url.pathname.replace(/\/\d+x\d+\//, "/"),
      url.search,
      secure,
      cache,
      contentType,
      fieldId,
    ]);
    let consumer = consumers.get(key);
    if (!consumer) {
      consumer = { controller: new AbortController(), count: 0 };
      consumers.set(key, consumer);
    }
    consumer.count++;
    let released = false;
    let cancelled;
    const cancellation = new Promise((resolve) => {
      cancelled = resolve;
    });
    const release = () => {
      if (released) return;
      released = true;
      signal?.removeEventListener("abort", release);
      cancelled({ ready: Promise.resolve("error") });
      if (--consumer.count === 0) {
        consumers.delete(key);
        consumer.controller.abort();
      }
    };
    signal?.addEventListener("abort", release, { once: true });
    try {
      const result = await Promise.race([requestImage({ ...query, signal: consumer.controller.signal }), cancellation]);
      const ready = Promise.race([result.ready, cancellation.then(() => "error")]);
      ready.finally(release);
      return { ...result, ready };
    } catch (error) {
      release();
      throw error;
    }
  });
};
